import { animated } from "@react-spring/web";
import classcat from "classcat";
import React from "react";

import { Button, IconStar, IconX } from "@touchtech/web-baselayer-ui";

import { Image } from "@is/components/Image";
import Price from "@is/components/Price";
import ProductMediaSlider from "@is/components/ProductMediaSlider";
import { MediaVariants } from "@is/constants";
import WidgetItem from "@is/features/ContentView/WidgetItem";
import {
  HTWIContentWidget,
  ProductItem as ProductItemType,
  WidgetType,
} from "@is/types";

import HTWIWidget from "../HTWIWidget";
import AddToCollectionModal from "./AddToCollectionModal";
import DescriptionAccordion from "./DescriptionAccordion";
import css from "./ProductDetailsItem.module.css";
import SizeSelector from "./SizeSelector";
import useManageProductDetails from "./hooks/useManageProductDetails";

type Props = {
  item: ProductItemType;
  itemIndex: number;
  contentItemId?: string;
  sectionId: string;
};

const ProductDetailsItem = ({
  item,
  itemIndex,
  contentItemId,
  sectionId,
}: Props) => {
  const {
    sizes,
    colorVariants,
    colorVariantsCollapsedLimit,
    styleDetails,
    colorDescription,
    lengths,
    instoreStockNotAvailable,
    onlineStockNotAvailable,
    selectedSize,
    selectedLength,
    selectedColorVariant,
    selectedSizeInfo,
    showSizeError,
    animationOverlayRef,
    styles,
    handleSelectColorVariant,
    handleSelectSize,
    handleSelectLength,
    handleBasketClick,
    handleShowMoreColorVariants,
    isPending,
    t,
    showOutOfStockAlarm,
    addToBasketButtonContent,
    isAdded,
    containerRef,
    howToWearItRef,
    widgets,
    handleShowMoreWidgetItems,
    handleShowAllWidgetItems,
    scrollFromWidgetToContainer,
    isLastItem,
    price,
    showMoreColorVariants,
    colorVariantsContainerHeight,
    isCheckoutEnabled,
    isCollectionModalOpen,
    handleClosePDP,
    handleCloseWidget,
    handleFavoritesClick,
    handleCollectionModalClose,
    notOnlyItemInSection,
    availableSizesForColorVariant,
    availableLengthsForColorVariant,
    showUnavailableSlash,
    availableVariantsWithSelectedSize,
    eventData,
    materialCompositionAndProductDescriptionEventData,
    showStoreFavoritesButton,
  } = useManageProductDetails({ item, itemIndex, sectionId });

  return (
    <>
      <div
        ref={containerRef}
        className={classcat([
          css.productDetailsItem,
          css.closableContainer,
          { [css.expanded]: item.isPDPOpen },
        ])}
      >
        <div>
          {showStoreFavoritesButton && (
            <button
              onClick={handleFavoritesClick}
              className={classcat([
                css.favoriteButtonContainer,
                { [css.onlyButton]: !notOnlyItemInSection },
              ])}
            >
              <IconStar className={css.favoriteIcon} />
            </button>
          )}
          {notOnlyItemInSection && (
            <div className={css.closeDetailsButton}>
              <Button
                onClick={handleClosePDP}
                className={"text-label-4xl"}
                sizing="xl"
                variant="tertiary"
              >
                <IconX />
              </Button>
            </div>
          )}
          <div className={classcat([css.container, css.anBackgroundAppear])}>
            <div className={css.animationFadeInSlider}>
              <ProductMediaSlider
                medias={selectedColorVariant?.media || []}
                itemIndex={itemIndex}
                eventData={eventData}
              />
            </div>
            <div className={css.productDetails}>
              <div className={css.productRow}>
                <div className={css.productInfo}>
                  <div
                    className={classcat([
                      "text-label-2xl",
                      css.animationFadeInFloatUp,
                      css.animationStep2,
                      css.productName,
                    ])}
                  >
                    {selectedColorVariant?.name || item.name}
                  </div>
                  {colorDescription && (
                    <div
                      className={classcat([
                        "text-label-lg",
                        css.colorName,
                        css.animationFadeInFloatUp,
                        css.animationStep2,
                      ])}
                    >
                      {colorDescription}
                    </div>
                  )}
                  {!!(price?.currencyCode && price?.retailPrice) && (
                    <div
                      className={classcat([
                        "text-label-2xl",
                        css.animationFadeInFloatUp,
                        css.animationStep3,
                      ])}
                    >
                      <Price price={price} />
                    </div>
                  )}
                </div>
                <div
                  className={classcat([css.rightColumn, css.sizesContainer])}
                >
                  {!!sizes.length && (
                    <div
                      className={classcat([
                        css.animationFadeInFloatUp,
                        css.animationStep2,
                      ])}
                    >
                      <div className={classcat(["text-label-lg", css.label])}>
                        {t("Size")}
                      </div>
                      <SizeSelector
                        onChange={handleSelectSize}
                        selectedItem={selectedSize}
                        items={sizes}
                        availableItems={availableSizesForColorVariant}
                      />
                    </div>
                  )}
                  {!!lengths.length && (
                    <div
                      className={classcat([
                        css.animationFadeInFloatUp,
                        css.animationStep4,
                      ])}
                    >
                      <div className={classcat(["text-label-lg", css.label])}>
                        {t("Length")}
                      </div>
                      <SizeSelector
                        onChange={handleSelectLength}
                        selectedItem={selectedLength}
                        items={lengths}
                        availableItems={availableLengthsForColorVariant}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className={css.productRow}>
                <div
                  className={classcat([
                    css.descriptionInfo,
                    css.animationFadeInFloatUp,
                    css.animationStep5,
                    {
                      [css.descriptionInfoSingleDescription]:
                        !styleDetails?.materialComposition?.length,
                    },
                  ])}
                >
                  <DescriptionAccordion
                    styleDetails={styleDetails}
                    eventData={
                      materialCompositionAndProductDescriptionEventData
                    }
                  />
                </div>
                <div
                  className={classcat([
                    css.rightColumn,
                    css.basketButtonContainer,
                    css.animationFadeInFloatUp,
                    css.animationStep6,
                  ])}
                >
                  <div className={css.sizeMessage}>
                    {!!selectedSizeInfo && (
                      <div className="text-body-lg">
                        {!instoreStockNotAvailable &&
                          (selectedSizeInfo.instoreQuantity > 0 ? (
                            <div className={css.stockInfo}>{t("In-store")}</div>
                          ) : (
                            <div
                              className={classcat([
                                css.stockInfo,
                                css.negative,
                              ])}
                            >
                              {t("Not in-store")}
                            </div>
                          ))}
                        {!onlineStockNotAvailable &&
                          (selectedSizeInfo.availableToOrder > 0 ? (
                            <div className={css.stockInfo}>
                              {t("Available to order")}
                            </div>
                          ) : (
                            <div
                              className={classcat([
                                css.stockInfo,
                                css.negative,
                              ])}
                            >
                              {t("Not available to order")}
                            </div>
                          ))}
                      </div>
                    )}
                    {showSizeError && (
                      <div
                        className={classcat(["text-body-lg", css.sizeError])}
                      >
                        {t("Please select size option")}
                      </div>
                    )}
                  </div>
                  <Button
                    className={classcat([
                      css.basketButton,
                      {
                        [css.added]: isAdded,
                        [css.outOfStock]: showOutOfStockAlarm,
                        [css.hidden]: !isCheckoutEnabled,
                      },
                    ])}
                    width="full"
                    variant="primary"
                    sizing="lg"
                    onClick={handleBasketClick}
                    state={isPending ? "pending" : undefined}
                  >
                    {addToBasketButtonContent}
                  </Button>
                </div>
              </div>
              {colorVariants?.length > 1 && (
                <div
                  className={css.colorVariantsContainer}
                  style={{ height: colorVariantsContainerHeight }}
                >
                  {colorVariants.map((colorVariant, index) => (
                    <div
                      key={colorVariant.id}
                      className={classcat([
                        css.colorVariantThumbnail,
                        css.animationFadeInFloatUp,
                        {
                          [css.animationStep7]:
                            colorVariantsCollapsedLimit - 1 > index &&
                            colorVariant.id !== selectedColorVariant?.id,
                          [css.selected]:
                            colorVariant.id === selectedColorVariant?.id,
                          [css.animationStep8]:
                            colorVariant.id === selectedColorVariant?.id,
                          [css.animationStep2]:
                            colorVariantsCollapsedLimit <= index,
                        },
                      ])}
                      data-id={colorVariant.id}
                      onClick={handleSelectColorVariant}
                    >
                      <Image
                        src={colorVariant.media.url}
                        layout="cover"
                        mediavariant={MediaVariants.MEDIUM}
                      />
                      {showUnavailableSlash &&
                      !availableVariantsWithSelectedSize.includes(
                        colorVariant.id
                      ) ? (
                        <div className={css.diagonal} />
                      ) : null}
                    </div>
                  ))}
                  {!showMoreColorVariants && (
                    <div
                      className={classcat([
                        "text-label-lg",
                        css.viewAllColors,
                        css.animationFadeInFloatUp,
                        css.animationStep7,
                      ])}
                      onClick={handleShowMoreColorVariants}
                    >
                      + {styleDetails?.variants?.length - colorVariants?.length}
                    </div>
                  )}
                </div>
              )}
            </div>
            <animated.div
              ref={animationOverlayRef}
              className={css.animationOverlay}
              style={styles}
            />
          </div>
        </div>
      </div>
      {widgets.map((widget, index) => (
        <div
          className={classcat([
            css.closableContainer,
            {
              [css.expanded]: item.isWidgetsOpen ? !widget.isClosed : false,
              [css.additionalPadding]: widgets.length - 1 === index,
            },
          ])}
          key={widget.id}
        >
          <div>
            {!widget.isClosed &&
              (widget.isExpanded ||
                (!!widget.items.length &&
                  widget.items.length <= 4 &&
                  notOnlyItemInSection) ||
                widget.type === WidgetType.HowToWearIt) && (
                <div className={css.closeDetailsButton}>
                  <Button
                    id={widget.id}
                    className={classcat(["text-label-4xl"])}
                    sizing="xl"
                    variant="tertiary"
                    onClick={handleCloseWidget}
                  >
                    <IconX />
                  </Button>
                </div>
              )}
            {widget.type === WidgetType.HowToWearIt ? (
              <HTWIWidget
                widget={widget as HTWIContentWidget}
                mainVariantId={selectedColorVariant?.id}
                onPDPItemClick={scrollFromWidgetToContainer}
                onShowMore={handleShowMoreWidgetItems}
                contentItemId={contentItemId}
                containerRef={howToWearItRef}
              />
            ) : (
              <WidgetItem
                widget={widget}
                onShowMore={handleShowMoreWidgetItems}
                onShowAll={handleShowAllWidgetItems}
                contentItemId={contentItemId}
              />
            )}
          </div>
        </div>
      ))}

      {isLastItem && !widgets?.length && <div className={css.stubContainer} />}
      {isCollectionModalOpen && (
        <AddToCollectionModal
          styleId={item.styleId}
          variantId={selectedColorVariant.id}
          isOpen
          onClose={handleCollectionModalClose}
        />
      )}
    </>
  );
};

export default ProductDetailsItem;
